import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import 'moment/locale/ru';
import { SearchablePicker, PcSelect } from '../../_Components';
import {
    SHIFT_KINDS,
    SHIFT_PRESETS,
    STATUS_OPTIONS,
    operatorName,
    operatorCode,
    matchesOperatorQuery,
    shiftKindKey,
    shiftTimes,
    minutesToTime,
    buildTimeOptions,
    buildSchedulePayload,
} from '../utils';

moment.locale('ru');

const EMPTY_WEEK_DATES = [];

const formatDayTitle = (value) => {
    const m = moment(value);
    const dow = m.format('dd');
    return `${dow.charAt(0).toUpperCase()}${dow.slice(1)} ${m.format('D MMMM')}`;
};

const kindByKey = SHIFT_KINDS.reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
}, {});

const ShiftModal = ({
    open,
    operator,
    operators = [],
    date,
    weekDates = EMPTY_WEEK_DATES,
    shift,
    onClose,
    onSave,
    onDelete,
}) => {
    const [operatorId, setOperatorId] = useState('');
    const [dayValue, setDayValue] = useState('');
    const [presetKey, setPresetKey] = useState('d');
    const [start, setStart] = useState(9 * 60);
    const [end, setEnd] = useState(17 * 60);
    const [status, setStatus] = useState('w');
    const [description, setDescription] = useState('');
    const [descError, setDescError] = useState(false);

    const selectedOperator = useMemo(() => {
        if (operator) return operator;
        return operators.find((item) => String(item.id || item._id) === String(operatorId)) || null;
    }, [operator, operators, operatorId]);

    const selectedDate = useMemo(() => {
        if (date) return date;
        const match = weekDates.find((item) => moment(item).format('YYYY-MM-DD') === dayValue);
        return match || weekDates[0] || moment();
    }, [date, weekDates, dayValue]);

    useEffect(() => {
        if (!open) return;
        setOperatorId(operator ? String(operator.id || operator._id) : '');
        setDayValue(moment(date || weekDates[0] || moment()).format('YYYY-MM-DD'));
        if (shift) {
            const times = shiftTimes(shift);
            const key = shiftKindKey(shift);
            const preset = SHIFT_PRESETS.find((item) => item.key === key);
            setPresetKey(preset ? preset.key : null);
            setStart(times.start);
            setEnd(times.end);
            setStatus(shift.change === 'ill' || shift.change === 'cencel' || shift.change === 'absenteeism' ? shift.change : 'w');
            setDescription(shift.description || '');
            setDescError(false);
        } else {
            const preset = SHIFT_PRESETS[1];
            setPresetKey(preset.key);
            setStart(preset.start);
            setEnd(preset.end);
            setStatus('w');
            setDescription('');
            setDescError(false);
        }
        // Reset only on open; later prop identity changes must not wipe the query.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const timeOptions = useMemo(() => {
        const times = buildTimeOptions(start);
        if (!times.includes(end)) times.push(end);
        return times.sort((a, b) => a - b);
    }, [start, end]);

    if (!open) return null;

    const applyPreset = (preset) => {
        setPresetKey(preset.key);
        setStart(preset.start);
        setEnd(preset.end);
        setStatus('w');
    };

    const handleSave = () => {
        if (!selectedOperator) return;
        const isEdit = !!(shift && (shift.id || shift._id));
        const note = description.trim();
        if (isEdit && !note) {
            setDescError(true);
            return;
        }
        const type = status === 'w'
            ? (SHIFT_PRESETS.find((item) => item.key === presetKey) || SHIFT_PRESETS[1]).type
            : (shift && shift.type) || 'day2';
        onSave(buildSchedulePayload({
            operator: selectedOperator,
            date: selectedDate,
            type,
            change: status === 'w' ? undefined : status,
            start,
            end,
            description: note,
        }));
    };

    return (
        <div className="pc-sched-ov" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
            <div className="pc-sched-modal" role="dialog" aria-modal="true">
                <div className="pc-sched-mh">
                    <div>
                        <h3>{operatorName(selectedOperator) || 'Смена'}</h3>
                        <div className="sub">{formatDayTitle(selectedDate)}</div>
                    </div>
                    <button type="button" className="pc-sched-btn pc-sched-btn--quiet" style={{ marginLeft: 'auto', width: 30, padding: 0 }} onClick={onClose} aria-label="Закрыть">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M4 4l8 8M12 4l-8 8" /></svg>
                    </button>
                </div>
                <div className="pc-sched-mb">
                    {!operator && (
                        <div className="pc-sched-fl" style={{ marginBottom: 12 }}>
                            <SearchablePicker
                                inputId="pc-sched-emp"
                                label="Сотрудник"
                                placeholder="Найти по имени или логину"
                                options={operators}
                                value={operatorId}
                                onChange={(item) => setOperatorId(item ? String(item.id || item._id) : '')}
                                getId={(item) => String(item.id || item._id)}
                                getLabel={operatorName}
                                getMeta={operatorCode}
                                filterItem={matchesOperatorQuery}
                                emptyText="Никого не найдено"
                                maxItems={40}
                                clearOnType
                            />
                        </div>
                    )}
                    {!date && weekDates.length > 0 && (
                        <div className="pc-sched-fl" style={{ marginBottom: 12 }}>
                            <label htmlFor="pc-sched-day">День</label>
                            <PcSelect
                                id="pc-sched-day"
                                value={dayValue}
                                onChange={(event) => setDayValue(event.target.value)}
                                options={weekDates.map((item) => ({
                                    id: moment(item).format('YYYY-MM-DD'),
                                    name: formatDayTitle(item),
                                }))}
                                searchable={false}
                            />
                        </div>
                    )}
                    <div className="pc-sched-presets">
                        {SHIFT_PRESETS.map((preset) => (
                            <button
                                key={preset.key}
                                type="button"
                                className={`pc-sched-pr${presetKey === preset.key ? ' sel' : ''}`}
                                onClick={() => applyPreset(preset)}
                            >
                                <b>
                                    <i style={{ background: kindByKey[preset.key].color }} />
                                    {preset.n}
                                </b>
                                <span>{minutesToTime(preset.start)} – {minutesToTime(preset.end)}</span>
                            </button>
                        ))}
                    </div>
                    <div className="pc-sched-g2">
                        <div className="pc-sched-fl">
                            <label htmlFor="pc-sched-start">Начало</label>
                            <PcSelect
                                id="pc-sched-start"
                                value={String(start)}
                                onChange={(event) => setStart(Number(event.target.value))}
                                options={timeOptions.map((value) => ({
                                    id: String(value),
                                    name: minutesToTime(value),
                                }))}
                                searchable
                                searchPlaceholder="Найти время"
                                mono
                            />
                        </div>
                        <div className="pc-sched-fl">
                            <label htmlFor="pc-sched-end">Конец</label>
                            <PcSelect
                                id="pc-sched-end"
                                value={String(end)}
                                onChange={(event) => setEnd(Number(event.target.value))}
                                options={timeOptions.map((value) => ({
                                    id: String(value),
                                    name: minutesToTime(value),
                                }))}
                                searchable
                                searchPlaceholder="Найти время"
                                mono
                            />
                        </div>
                    </div>
                    <div className="pc-sched-fl" style={{ marginTop: 12 }}>
                        <label htmlFor="pc-sched-status">Статус</label>
                        <PcSelect
                            id="pc-sched-status"
                            value={status}
                            onChange={(event) => setStatus(event.target.value)}
                            options={STATUS_OPTIONS.map((item) => ({ id: item.id, name: item.n }))}
                            searchable={false}
                        />
                    </div>
                    <div className="pc-sched-fl" style={{ marginTop: 12 }}>
                        <label htmlFor="pc-sched-desc">
                            Описание{shift && (shift.id || shift._id) ? <i> *</i> : null}
                        </label>
                        <textarea
                            id="pc-sched-desc"
                            className={`pc-sched-inp pc-sched-area${descError ? ' is-err' : ''}`}
                            rows={3}
                            value={description}
                            placeholder={shift && (shift.id || shift._id) ? 'Почему меняете смену' : 'Комментарий к смене'}
                            onChange={(event) => {
                                setDescription(event.target.value);
                                if (event.target.value.trim()) setDescError(false);
                            }}
                        />
                        {descError ? <span className="pc-sched-err">Укажите описание — оно обязательно при изменении смены</span> : null}
                    </div>
                </div>
                <div className="pc-sched-mf">
                    <button
                        type="button"
                        className="pc-sched-btn pc-sched-btn--pri"
                        disabled={!selectedOperator || (!!shift && !!(shift.id || shift._id) && !description.trim())}
                        onClick={handleSave}
                    >Сохранить</button>
                    <button type="button" className="pc-sched-btn" onClick={onClose}>Отмена</button>
                    {shift && onDelete && (
                        <button type="button" className="pc-sched-btn pc-sched-btn--danger" onClick={() => onDelete(shift.id || shift._id)}>
                            Убрать смену
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShiftModal;
